import { DestroyRef, Injectable, inject } from '@angular/core'

import { BehaviorSubject, Observable, ReplaySubject, finalize, first, of, tap } from 'rxjs'

import { FlxModalRef, FlxModalService } from '@flowx/angular-ui-toolkit'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'

import { showModalAboveAiPanel } from '../utils/modal.utils'

export interface GuardConfig {
  standaloneMessage: string
  compoundMessage: string
  confirmText?: string
  cancelText?: string
  appendTo?: string | HTMLElement
  /** Modal heading. The guard that opens the session wins; defaults to 'Unsaved changes' */
  title?: string
  /** Class on the modal panel — 'pending-changes' is the 600px-wide variant */
  modalClass?: string
  /** Class on the CDK backdrop — 'overlay-backdrop' sits above the branching overlay */
  backdropClass?: string
}

/** A surface holding unsaved edits, registered for as long as it is on screen */
export interface UnsavedChangesSource {
  hasUnsavedChanges: () => boolean
  discard: () => void
}

/**
 * The single gate for actions that cost the user unfinished work.
 *
 * Two entry points, one modal. `registerGuardCheck()` is the ad-hoc form — a caller supplies its
 * own reason, and concurrent callers coalesce into one prompt listing every reason.
 * `confirmProceed()` is the registry form: surfaces opt in via `registerSource()` and actions ask
 * one question without knowing which surface holds the edits.
 */
@Injectable({
  providedIn: 'root',
})
export class GuardCoordinatorService {
  #modalService = inject(FlxModalService)
  #activeModalRef: FlxModalRef | null = null
  #sharedDecision$: ReplaySubject<boolean> | null = null
  #pendingGuards: GuardConfig[] = []
  #modalMessage$: BehaviorSubject<string | string[]> | null = null

  #isModalPending = false
  #cleanupCalled = false
  #sources = new Set<UnsavedChangesSource>()

  readonly #DEFAULT_CONFIRM_TEXT = 'Leave page'
  readonly #DEFAULT_CANCEL_TEXT = 'Stay'
  readonly #DEFAULT_TITLE = 'Unsaved changes'

  registerGuardCheck(config: GuardConfig): Observable<boolean> {
    const guardConfig: GuardConfig = {
      standaloneMessage: config.standaloneMessage,
      compoundMessage: config.compoundMessage,
      confirmText: config.confirmText ?? this.#DEFAULT_CONFIRM_TEXT,
      cancelText: config.cancelText ?? this.#DEFAULT_CANCEL_TEXT,
      appendTo: config.appendTo,
      title: config.title ?? this.#DEFAULT_TITLE,
      modalClass: config.modalClass,
      backdropClass: config.backdropClass,
    }

    // If modal is already created, append guard info and return shared observable
    if (this.#isModalPending && this.#sharedDecision$) {
      this.#pendingGuards.push(guardConfig)
      this.#updateModalMessage()
      this.#updateModalPosition()
      return this.#sharedDecision$.asObservable()
    }

    // First guard check - initialize new session and create modal immediately
    this.#initializeNewSession(guardConfig)

    return this.#sharedDecision$.asObservable().pipe(
      finalize(() => {
        this.#cleanup()
      })
    )
  }

  /** Registers a dirty surface for as long as `destroyRef` lives, so `confirmProceed()` can ask it */
  registerSource(source: UnsavedChangesSource, destroyRef: DestroyRef): void {
    this.#sources.add(source)
    destroyRef.onDestroy(() => this.#sources.delete(source))
  }

  /**
   * Emits `true` when the action may run — immediately if nothing is dirty, otherwise once the user
   * confirms. Confirming discards every dirty surface, which also leaves the `canDeactivate` guards
   * quiet for actions that navigate afterwards.
   *
   * Route changes are covered by `canDeactivate`. Everything else — query-param-only navigation,
   * modals, in-place mutations — never deactivates a route, so those actions have to ask here.
   */
  confirmProceed(): Observable<boolean> {
    const dirtySources = [...this.#sources].filter((source) => source.hasUnsavedChanges())

    if (!dirtySources.length) {
      return of(true)
    }

    return this.registerGuardCheck({
      title: 'Changes Unsaved',
      standaloneMessage: 'You have unsaved changes. Are you sure you want to continue?',
      compoundMessage: 'Discard unsaved changes',
      confirmText: 'Discard Changes',
      cancelText: 'Cancel',
      modalClass: 'pending-changes',
      backdropClass: 'overlay-backdrop',
    }).pipe(
      first(),
      tap((confirm) => {
        if (confirm) {
          dirtySources.forEach((source) => source.discard())
        }
      })
    )
  }

  reset(): void {
    if (this.#activeModalRef) {
      this.#activeModalRef.close()
    }
    this.#cleanup()
  }

  #initializeNewSession(guardConfig: GuardConfig): void {
    this.#isModalPending = true
    this.#cleanupCalled = false
    this.#pendingGuards = [guardConfig]
    this.#sharedDecision$ = new ReplaySubject<boolean>(1)
    this.#modalMessage$ = new BehaviorSubject<string | string[]>(this.#formatMessage())

    const isSingleGuard = this.#pendingGuards.length === 1
    const btnOkText = isSingleGuard
      ? this.#pendingGuards[0].confirmText
      : this.#DEFAULT_CONFIRM_TEXT
    const btnCancelText = isSingleGuard
      ? this.#pendingGuards[0].cancelText
      : this.#DEFAULT_CANCEL_TEXT

    const appendTo = this.#determineAppendTo()

    const modalConfig: any = {
      closeOnBackdropClick: false,
    }

    if (appendTo !== undefined) {
      modalConfig.appendTo = appendTo
    }

    if (guardConfig.backdropClass) {
      modalConfig.backdropClass = guardConfig.backdropClass
    }

    this.#activeModalRef = showModalAboveAiPanel(
      this.#modalService,
      ConfirmationModalComponent,
      modalConfig,
      {
        title: guardConfig.title,
        message$: this.#modalMessage$.asObservable(),
        btnOkText,
        btnCancelText,
        btnOkType: 'danger',
        modalClass: guardConfig.modalClass,
        // This service closes the overlay itself on confirm — see the data$ handler below
        closeModalOnConfirm: false,
      }
    )

    // Set once the user clicks, so the teardown that follows a click is not read as a dismissal
    let answered = false

    this.#activeModalRef.data$
      .pipe(
        tap((data: { confirm: boolean }) => {
          answered = true
          // Close before publishing the decision. FlxModalService.hide() pops whatever is last on
          // its LIFO stack, so a subscriber that opens a modal synchronously off this decision
          // would otherwise get its own modal disposed instead of this one. Only the confirm path
          // needs it: ConfirmationModalComponent.cancel() still closes itself, and nothing opens a
          // modal from a cancelled guard. hide() rather than #activeModalRef.close() keeps the
          // service's #overlayRefs in step — a stranded ref would absorb a later hide() and leave
          // a visible modal on screen.
          if (data.confirm) {
            this.#modalService.hide()
          }
          if (this.#sharedDecision$) {
            this.#sharedDecision$.next(data.confirm)
          }
        })
      )
      .subscribe()

    // Browser back/forward disposes the overlay without a click (FlxModalService turns on CDK's
    // disposeOnNavigation), so data$ never emits. Left open, the session would stay pending and every
    // later guard check — the popstate navigation's own canDeactivate included — would join it and
    // wait on a modal that is gone. Settle it as "Stay": the abandoned action backs off, and the
    // back navigation asks again from a fresh session. hide() goes first, as on the confirm path: CDK
    // disposed the overlay behind FlxModalService's back, and the ref it strands on the stack would
    // absorb the next hide() meant for a modal still on screen.
    const decision$ = this.#sharedDecision$
    this.#activeModalRef.componentRef.onDestroy(() => {
      if (!answered) {
        this.#modalService.hide()
        decision$.next(false)
      }
    })
  }

  #updateModalMessage(): void {
    if (this.#modalMessage$) {
      this.#modalMessage$.next(this.#formatMessage())
    }
  }

  #updateModalPosition(): void {
    if (!this.#activeModalRef) {
      return
    }

    // Determine the target appendTo based on all registered guards
    const targetAppendTo = this.#determineAppendTo()

    // Only move if we have an explicit target
    if (targetAppendTo !== undefined) {
      this.#activeModalRef.moveTo(targetAppendTo)
    }
  }

  #determineAppendTo(): string | HTMLElement | undefined {
    const bodyGuard = this.#pendingGuards.find((g) => g.appendTo === 'body')
    if (bodyGuard) {
      return 'body'
    }

    return this.#pendingGuards.find((g) => g.appendTo)?.appendTo
  }

  #formatMessage(): string | string[] {
    if (this.#pendingGuards.length === 1) {
      const guard = this.#pendingGuards[0]
      return guard.standaloneMessage
    }

    return this.#pendingGuards.map((guard) => guard.compoundMessage)
  }

  #cleanup(): void {
    if (this.#cleanupCalled) {
      return
    }
    this.#cleanupCalled = true

    this.#isModalPending = false
    this.#activeModalRef = null
    this.#sharedDecision$ = null
    this.#pendingGuards = []

    if (this.#modalMessage$ && !this.#modalMessage$.closed) {
      this.#modalMessage$.complete()
    }
    this.#modalMessage$ = null
  }
}
